import { useCallback, useEffect, useMemo } from "react";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { JSONSchemaForm } from "@/components/JSONSchemaForm";
import {
  JSONObject,
  isJSONObject,
  type JSONValue,
} from "@/components/jsonSchemaForm/types";
import {
  CustomFieldMappingSection,
  type CustomFieldMappingValue,
} from "@/components/erp/CustomFieldMappingSection";
import { OpportunityFieldMappingSection } from "@/components/erp/OpportunityFieldMappingSection";
import { QuotedByMappingSection } from "@/components/erp/QuotedByMappingSection";
import type { MappingSchema } from "@/components/erp/customFieldMappingHelpers";
import type { JSONSchema7 } from "json-schema";
import { cn } from "@/lib/utils";
import type { ERPIntegrationProviderMetadata } from "@/store/erpIntegrationsStore";
import type { ConnectionCapabilities } from "@forgepoint/shared";
import { HUBSPOT_APP_ID } from "@forgepoint/shared";
import { useConnectionsStore } from "@/store/connectionsStore";
import { useItemSettingsStore } from "@/store/itemSettingsStore";
import { useQuoteSettingsStore } from "@/store/quoteSettingsStore";
import { Info } from "lucide-react";

interface SyncToggleDefinition {
  id: string;
  label: string;
  description: string;
  /** Capability that must be granted for the toggle to be editable. */
  capability: keyof ConnectionCapabilities;
  /** HubSpot scope hint shown when the capability is missing. */
  hubspotScope?: string;
}

const SYNC_TOGGLES: readonly SyncToggleDefinition[] = [
  {
    id: "syncCompanies",
    label: "Pull Companies",
    description: "Pull companies from this integration",
    capability: "canPullCompanies",
    hubspotScope: "Requires the Companies (read) scope",
  },
  {
    id: "syncItems",
    label: "Pull Items",
    description:
      "Pull parts and their approved revisions from this integration",
    capability: "canPullItems",
    hubspotScope: "Not supported by HubSpot",
  },
  {
    id: "syncOperations",
    label: "Pull Operations",
    description: "Pull operations and resource rates from this integration",
    capability: "canPullOperations",
    hubspotScope: "Not supported by HubSpot",
  },
  {
    id: "syncPriceLists",
    label: "Pull Price Lists",
    description:
      "Pull price lists and their customer assignments from this integration",
    capability: "canPullPriceLists",
    hubspotScope: "Not supported by HubSpot",
  },
  {
    id: "syncPeople",
    label: "Pull People",
    description: "Pull people from this integration",
    capability: "canPullPeople",
    hubspotScope: "Requires the Contacts (read) scope",
  },
  {
    id: "syncOpportunities",
    label: "Pull Opportunities",
    description: "Pull updates for opportunities created in Forgepoint",
    capability: "canPullOpportunities",
    hubspotScope: "Requires the Deals (read) scope",
  },
  {
    id: "syncLocations",
    label: "Pull Locations",
    description: "Pull locations from this integration",
    capability: "canPullLocations",
    hubspotScope: "Not supported by HubSpot",
  },
  {
    id: "syncInventoryLines",
    label: "Pull Inventory",
    description:
      "Pull available stock for pulled items and locations from this integration",
    capability: "canPullInventoryLines",
    hubspotScope: "Not supported by HubSpot",
  },
  {
    id: "pushCompanies",
    label: "Push Companies",
    description: "Push companies to this integration",
    capability: "canPushCompanies",
    hubspotScope: "Requires the Companies (write) scope",
  },
  {
    id: "pushPeople",
    label: "Push People",
    description: "Push people to this integration",
    capability: "canPushPeople",
    hubspotScope: "Requires the Contacts (write) scope",
  },
  {
    id: "pushOpportunities",
    label: "Push Opportunities",
    description: "Push opportunities to this integration",
    capability: "canPushOpportunities",
    hubspotScope: "Requires the Deals (write) scope",
  },
  {
    id: "pushQuotes",
    label: "Push Quotes",
    description:
      "Push finalized quotes, and the items they depend on, to this integration",
    capability: "canPushQuotes",
    hubspotScope: "Not supported by HubSpot",
  },
];

// Fields rendered by dedicated mapping sections or capability-gated toggles.
const STRIPPED_FIELDS = [
  "opportunityCustomFieldMapping",
  "opportunityFieldMapping",
  "itemCustomFieldMapping",
  "quoteCustomFieldMapping",
  "quoteLineCustomFieldMapping",
  "companyCustomFieldMapping",
  "quotedByEmployeeByUserId",
  "statusMapping", // legacy category-based mapping, superseded by stageMapping
  ...SYNC_TOGGLES.map(({ id }) => id),
];

interface ERPSyncFormFieldsProps {
  /** Current integrationConfig form data */
  config: JSONObject;
  /** Called when the integrationConfig form changes */
  onConfigChange: (config: JSONObject) => void;
  /** Provider's syncConfigSchema, if any. Renders a JSONSchemaForm when present. */
  configSchema: JSONSchema7 | null;
  /** Whether the connection is ready for scanning (pull/push). */
  canScan?: boolean;
  /** Operations supported by the selected provider and connection. */
  capabilities?: ConnectionCapabilities | null;
  /** The active ERP provider metadata (needed for re-authorize). */
  activeProvider?: ERPIntegrationProviderMetadata | null;
}

/**
 * Connection-dependent configuration fields shared by sync creation and editing.
 * @param props - ERPSyncFormFieldsProps
 * @returns The form fields element
 */
export function ERPSyncFormFields({
  config,
  onConfigChange,
  configSchema,
  canScan = true,
  capabilities,
  activeProvider,
}: ERPSyncFormFieldsProps) {
  const generateConnectionToken = useConnectionsStore(
    (s) => s.generateConnectionToken,
  );
  const itemSettings = useItemSettingsStore((state) => state.settings);
  const itemSettingsFetched = useItemSettingsStore((state) => state.hasFetched);
  const fetchItemSettings = useItemSettingsStore(
    (state) => state.fetchSettings,
  );
  const quoteSettings = useQuoteSettingsStore((state) => state.settings);
  const fetchQuoteSettings = useQuoteSettingsStore(
    (state) => state.fetchSettings,
  );
  const isHubSpot = activeProvider?.id === "hubspot";

  const hasMissingHubSpotCapability =
    isHubSpot &&
    !!capabilities &&
    (!capabilities.canPullOpportunities ||
      !capabilities.canPushOpportunities ||
      !capabilities.canPullCompanies ||
      !capabilities.canPushCompanies ||
      !capabilities.canPullPeople ||
      !capabilities.canPushPeople ||
      !capabilities.canResolveOwners ||
      !capabilities.canPushAttachments);
  const syncToggles = SYNC_TOGGLES.filter(
    ({ id }) => configSchema?.properties?.[id] !== undefined,
  );
  const unavailableTooltip = (scopeMessage: string) =>
    isHubSpot
      ? scopeMessage
      : `Not supported by ${activeProvider?.name ?? "this provider"}`;

  const handleReauthorize = useCallback(async () => {
    if (!activeProvider || !isHubSpot) {
      return;
    }
    const tokenResponse = await generateConnectionToken(
      activeProvider.requiredConnectionProvider.providerId,
      HUBSPOT_APP_ID,
    );
    if (!tokenResponse?.authorizationUrl) {
      return;
    }
    window.location.href = tokenResponse.authorizationUrl;
  }, [activeProvider, generateConnectionToken, isHubSpot]);
  const opportunityCustomFieldMappingSchema =
    useMemo((): MappingSchema | null => {
      const raw = configSchema?.properties?.opportunityCustomFieldMapping;
      if (
        raw === undefined ||
        typeof raw === "boolean" ||
        raw.properties === undefined ||
        Object.keys(raw.properties).length === 0
      ) {
        return null;
      }
      return raw;
    }, [configSchema]);

  const opportunityFieldMappingSchema = useMemo((): MappingSchema | null => {
    const raw = configSchema?.properties?.opportunityFieldMapping;
    if (
      raw === undefined ||
      typeof raw === "boolean" ||
      raw.properties === undefined ||
      Object.keys(raw.properties).length === 0
    ) {
      return null;
    }
    return raw;
  }, [configSchema]);
  const mappingSchemas = useMemo(
    () => ({
      item: getMappingSchema(configSchema, "itemCustomFieldMapping"),
      quote: getMappingSchema(configSchema, "quoteCustomFieldMapping"),
      quoteLine: getMappingSchema(configSchema, "quoteLineCustomFieldMapping"),
      company: getMappingSchema(configSchema, "companyCustomFieldMapping"),
      quotedBy: getMappingSchema(configSchema, "quotedByEmployeeByUserId"),
    }),
    [configSchema],
  );

  useEffect(() => {
    if (mappingSchemas.item && !itemSettingsFetched) {
      fetchItemSettings();
    }
    if ((mappingSchemas.quote || mappingSchemas.quoteLine) && !quoteSettings) {
      fetchQuoteSettings();
    }
  }, [
    mappingSchemas,
    fetchItemSettings,
    fetchQuoteSettings,
    itemSettingsFetched,
    quoteSettings,
  ]);

  const currentMapping = parseCustomFieldMappingRecord(
    config.opportunityCustomFieldMapping,
  );
  const currentItemMapping = parseCustomFieldMappingRecord(
    config.itemCustomFieldMapping,
  );
  const currentQuoteMapping = parseCustomFieldMappingRecord(
    config.quoteCustomFieldMapping,
  );
  const currentQuoteLineMapping = parseCustomFieldMappingRecord(
    config.quoteLineCustomFieldMapping,
  );
  const currentCompanyMapping = parseCustomFieldMappingRecord(
    config.companyCustomFieldMapping,
  );

  const currentFieldMapping = parseStringRecord(config.opportunityFieldMapping);
  const currentQuotedByMapping = parseStringRecord(
    config.quotedByEmployeeByUserId,
  );

  const jsonFormSchema = useMemo(() => {
    if (!configSchema?.properties) {
      return configSchema;
    }
    const filtered = Object.fromEntries(
      Object.entries(configSchema.properties).filter(
        ([key]) => !STRIPPED_FIELDS.includes(key),
      ),
    );
    return { ...configSchema, properties: filtered };
  }, [configSchema]);

  return (
    <div className="space-y-5">
      {!canScan && (
        <div className="flex animate-in fade-in duration-300 items-start gap-2 text-sm text-muted-foreground">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          {isHubSpot ? (
            <span>
              This HubSpot connection needs to be re-authorized before syncing
              can proceed.{" "}
              <button
                type="button"
                className="underline underline-offset-2 transition-colors hover:text-foreground"
                onClick={handleReauthorize}
              >
                Re-authorize HubSpot
              </button>
            </span>
          ) : (
            <span>
              This connection is not ready to sync. Reconnect it and try again.
            </span>
          )}
        </div>
      )}

      {canScan && hasMissingHubSpotCapability && (
        <div className="flex animate-in fade-in duration-300 items-start gap-2 text-sm text-muted-foreground">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            Some features are unavailable because optional HubSpot scopes were
            not granted.{" "}
            <button
              type="button"
              className="underline underline-offset-2 transition-colors hover:text-foreground"
              onClick={handleReauthorize}
            >
              Re-authorize HubSpot
            </button>
          </span>
        </div>
      )}

      {syncToggles.length > 0 && (
        <TooltipProvider>
          <div className="space-y-3 border-t pt-5">
            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Sync Toggles
            </span>
            {syncToggles.map((toggle) =>
              renderSyncToggle({
                id: toggle.id,
                label: toggle.label,
                description: toggle.description,
                checked: config[toggle.id] === true,
                disabled: !!capabilities && !capabilities[toggle.capability],
                tooltip: toggle.hubspotScope
                  ? unavailableTooltip(toggle.hubspotScope)
                  : undefined,
                onCheckedChange: (enabled) =>
                  onConfigChange({ ...config, [toggle.id]: enabled }),
              }),
            )}
          </div>
        </TooltipProvider>
      )}

      {isHubSpot && !!capabilities && !capabilities.canPushAttachments && (
        <div className="flex animate-in fade-in duration-300 items-start gap-2 text-sm text-muted-foreground">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            Notes will sync without file attachments because the Files scope was
            not granted.
          </span>
        </div>
      )}

      {jsonFormSchema && (
        <div className="space-y-3 border-t pt-5">
          <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Configuration
          </span>
          <JSONSchemaForm
            schema={jsonFormSchema}
            formData={config}
            onChange={(data) => {
              if (!isJSONObject(data)) {
                return;
              }
              onConfigChange(data);
            }}
            showSubmitButton={false}
          />
        </div>
      )}

      {opportunityFieldMappingSchema && (
        <OpportunityFieldMappingSection
          mapping={currentFieldMapping}
          onMappingChange={(mapping) =>
            onConfigChange({
              ...config,
              opportunityFieldMapping: mapping,
            })
          }
          mappingSchema={opportunityFieldMappingSchema}
          capabilities={capabilities}
          providerId={activeProvider?.id ?? null}
        />
      )}

      {opportunityCustomFieldMappingSchema && (
        <CustomFieldMappingSection
          mapping={currentMapping}
          onMappingChange={(mapping) =>
            onConfigChange({
              ...config,
              opportunityCustomFieldMapping: customFieldMappingToJSON(mapping),
            })
          }
          mappingSchema={opportunityCustomFieldMappingSchema}
          capabilities={capabilities}
          providerId={activeProvider?.id ?? null}
        />
      )}

      {mappingSchemas.item && itemSettings?.customFieldsSchema && (
        <CustomFieldMappingSection
          title="Item Custom Field Mapping"
          mapping={currentItemMapping}
          onMappingChange={(mapping) =>
            onConfigChange({
              ...config,
              itemCustomFieldMapping: customFieldMappingToJSON(mapping),
            })
          }
          mappingSchema={mappingSchemas.item}
          sourceSchema={itemSettings.customFieldsSchema}
          capabilities={capabilities}
          providerId={activeProvider?.id ?? null}
        />
      )}

      {mappingSchemas.quote && quoteSettings?.customFieldsSchema && (
        <CustomFieldMappingSection
          title="Quote Custom Field Mapping"
          mapping={currentQuoteMapping}
          onMappingChange={(mapping) =>
            onConfigChange({
              ...config,
              quoteCustomFieldMapping: customFieldMappingToJSON(mapping),
            })
          }
          mappingSchema={mappingSchemas.quote}
          sourceSchema={quoteSettings.customFieldsSchema}
          capabilities={capabilities}
          providerId={activeProvider?.id ?? null}
        />
      )}

      {mappingSchemas.quoteLine &&
        quoteSettings?.lineItemCustomFieldsSchema && (
          <CustomFieldMappingSection
            title="Quote Line Custom Field Mapping"
            mapping={currentQuoteLineMapping}
            onMappingChange={(mapping) =>
              onConfigChange({
                ...config,
                quoteLineCustomFieldMapping: customFieldMappingToJSON(mapping),
              })
            }
            mappingSchema={mappingSchemas.quoteLine}
            sourceSchema={quoteSettings.lineItemCustomFieldsSchema}
            capabilities={capabilities}
            providerId={activeProvider?.id ?? null}
          />
        )}

      {mappingSchemas.company && (
        <CustomFieldMappingSection
          title="Company Custom Field Mapping"
          mapping={currentCompanyMapping}
          onMappingChange={(mapping) =>
            onConfigChange({
              ...config,
              companyCustomFieldMapping: customFieldMappingToJSON(mapping),
            })
          }
          mappingSchema={mappingSchemas.company}
          capabilities={capabilities}
          providerId={activeProvider?.id ?? null}
        />
      )}

      {mappingSchemas.quotedBy && (
        <QuotedByMappingSection
          mapping={currentQuotedByMapping}
          onMappingChange={(mapping) =>
            onConfigChange({ ...config, quotedByEmployeeByUserId: mapping })
          }
          mappingSchema={mappingSchemas.quotedBy}
        />
      )}
    </div>
  );
}

interface SyncToggleProps {
  id: string;
  label: string;
  description: string;
  checked: boolean;
  disabled: boolean;
  tooltip?: string;
  onCheckedChange: (checked: boolean) => void;
}

/**
 * Renders a sync toggle row with an optional tooltip when the toggle is
 * disabled because the provider or connection does not support it.
 * @param props - SyncToggleProps
 * @returns The toggle row element
 */
function renderSyncToggle({
  id,
  label,
  description,
  checked,
  disabled,
  tooltip,
  onCheckedChange,
}: SyncToggleProps) {
  return (
    <div
      key={id}
      className={cn(
        "flex items-center justify-between transition-opacity duration-200",
        disabled && "opacity-50",
      )}
    >
      <div className="flex flex-col gap-0.5">
        <Label htmlFor={id} className="text-sm font-medium">
          {label}
        </Label>
        <span className="text-xs text-muted-foreground">{description}</span>
      </div>
      <div className="flex items-center gap-1.5">
        {disabled && tooltip && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className="h-4 w-4 text-muted-foreground" />
            </TooltipTrigger>
            <TooltipContent>
              <p>{tooltip}</p>
            </TooltipContent>
          </Tooltip>
        )}
        <Switch
          id={id}
          checked={disabled ? false : checked}
          onCheckedChange={onCheckedChange}
          disabled={disabled}
        />
      </div>
    </div>
  );
}

/**
 * Converts a custom field mapping record to a JSONObject-compatible value.
 * Select field mapping objects (`{ erpProperty, optionMapping }`) are converted
 * to plain `JSONObject` so they satisfy the generic form data type.
 * @param mapping - The custom field mapping with typed values
 * @returns A JSONObject-compatible mapping
 */
/**
 * Parses a JSONValue into a Record<string, CustomFieldMappingValue> by
 * validating each entry is either a string or a SelectFieldMappingValue.
 * Returns an empty record if the value is not a valid mapping object.
 * @param value - Any JSONValue to parse
 * @returns A properly typed custom field mapping record
 */
function parseCustomFieldMappingRecord(
  value: JSONValue,
): Record<string, CustomFieldMappingValue> {
  if (!isJSONObject(value)) {
    return {};
  }
  const result: Record<string, CustomFieldMappingValue> = {};
  for (const [key, v] of Object.entries(value)) {
    if (typeof v === "string") {
      result[key] = v;
      continue;
    }
    if (
      isJSONObject(v) &&
      typeof v.erpProperty === "string" &&
      isJSONObject(v.optionMapping)
    ) {
      const optionMapping: Record<string, string> = {};
      for (const [optKey, optVal] of Object.entries(v.optionMapping)) {
        if (typeof optVal === "string") {
          optionMapping[optKey] = optVal;
        }
      }
      result[key] = { erpProperty: v.erpProperty, optionMapping };
    }
  }
  return result;
}

/**
 * Parses a JSONValue into a Record<string, string> by extracting only
 * string-valued entries. Returns an empty record if the value is not a
 * valid object.
 * @param value - Any JSONValue to parse
 * @returns A properly typed string record
 */
function parseStringRecord(value: JSONValue): Record<string, string> {
  if (!isJSONObject(value)) {
    return {};
  }
  const result: Record<string, string> = {};
  for (const [key, v] of Object.entries(value)) {
    if (typeof v === "string") {
      result[key] = v;
    }
  }
  return result;
}

function customFieldMappingToJSON(
  mapping: Record<string, CustomFieldMappingValue>,
): JSONObject {
  const result: JSONObject = {};
  for (const [key, value] of Object.entries(mapping)) {
    if (typeof value === "string") {
      result[key] = value;
    } else {
      result[key] = {
        erpProperty: value.erpProperty,
        optionMapping: { ...value.optionMapping },
      };
    }
  }
  return result;
}

function getMappingSchema(
  configSchema: JSONSchema7 | null,
  field: string,
): MappingSchema | null {
  const raw = configSchema?.properties?.[field];
  if (
    raw === undefined ||
    typeof raw === "boolean" ||
    raw.properties === undefined ||
    Object.keys(raw.properties).length === 0
  ) {
    return null;
  }
  return raw;
}
